import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import { kits, supabase } from "../../services/supabase";
import { api, FALLBACK_PRICING_TIERS, PricingTier, calculateUnitPrice } from "../../services/api";
import { Kit } from "../../types/database";
import SiteNav from "../../components/SiteNav";
import "./orderkit.css";

const OrderKit: React.FC = () => {
  const { user, username } = useUser();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const kitId = searchParams.get("kitId");

  const [kit, setKit] = useState<Kit | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [quantities, setQuantities] = useState<Record<string, number>>({
    XS: 0, S: 0, M: 0, L: 0, XL: 0, XXL: 0,
  });
  const [address, setAddress] = useState({
    line1: "", line2: "", city: "", postcode: "",
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [tiers, setTiers] = useState<PricingTier[]>(FALLBACK_PRICING_TIERS);

  const sizes = ["XS", "S", "M", "L", "XL", "XXL"];

  useEffect(() => {
    if (!kitId) { navigate("/", { state: { scrollTo: "tribes" } }); return; }
    kits.getById(kitId)
      .then(({ data, error }) => {
        if (error) throw error;
        setKit(data);
      })
      .catch(() => { alert("Failed to load kit details"); navigate("/", { state: { scrollTo: "tribes" } }); })
      .finally(() => setLoading(false));
  }, [kitId, navigate]);

  useEffect(() => {
    api.getPricing()
      .then(({ tiers }) => setTiers(tiers))
      .catch(() => { /* keep fallback tiers */ });
  }, []);

  const updateQuantity = (size: string, value: number) =>
    setQuantities(prev => ({ ...prev, [size]: Math.max(0, value) }));

  const totalItems = Object.values(quantities).reduce((s, q) => s + q, 0);
  const unitPrice = calculateUnitPrice(totalItems, tiers);
  const total = totalItems * unitPrice;

  const handleCheckout = async () => {
    if (!user) { navigate("/account"); return; }
    if (totalItems === 0) { alert("Please select at least one item."); return; }
    if (!address.line1.trim() || !address.city.trim() || !address.postcode.trim()) {
      alert("Please enter your full delivery address.");
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { navigate("/account"); return; }

    setIsSubmitting(true);
    try {
      const kit_snapshot = {
        design_name:         kit?.design_name || "",
        kit_type:            kit?.kit_type || "polo",
        teamwear_color:      kit?.teamwear_color || "#1E3A8A",
        emblem_color:        kit?.emblem_color || "#FFFFFF",
        preview_front_url:   kit?.preview_front_url || "",
        preview_back_url:    kit?.preview_back_url || "",
        front_image_url:     kit?.front_image_url || "",
        back_image_url:      kit?.back_image_url || "",
        back_print_text:     kit?.back_print_text || "",
        back_print_enabled:  kit?.back_print_enabled ? "True" : "False",
        back_print_position: kit?.back_print_position || 50,
        team_name:           kit?.team_name || "",
      };

      const { checkout_url } = await api.createCheckoutSession({
        kit_id:                   kitId,
        team_id:                  kit?.team_id,
        quantities,
        kit_snapshot,
        customer_name:            username,
        team_name:                kit?.team_name,
        design_name:              kit?.design_name,
        delivery_address_line1:   address.line1.trim(),
        delivery_address_line2:   address.line2.trim(),
        delivery_city:            address.city.trim(),
        delivery_postcode:        address.postcode.trim().toUpperCase(),
      }, session.access_token);

      window.location.href = checkout_url;
    } catch (err: any) {
      console.error("Checkout error:", err);
      alert(`Checkout failed: ${err.message}`);
      setIsSubmitting(false);
    }
  };

  if (loading) return (
    <div className="ok-page">
      <div className="grain-overlay" />
      <SiteNav />
      <div className="ok-content">
        <p className="ok-loading">Loading kit details…</p>
      </div>
    </div>
  );

  if (!kit) return (
    <div className="ok-page">
      <div className="grain-overlay" />
      <SiteNav />
      <div className="ok-content">
        <div className="ok-empty">
          <div className="ok-empty-title">Kit not found</div>
          <button className="ok-checkout-btn" onClick={() => navigate("/", { state: { scrollTo: "tribes" } })}>
            Back to Teams →
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="ok-page">
      <div className="grain-overlay" />
      <SiteNav />

      <div className="ok-content">
        <button className="ok-back" onClick={() => navigate(`/team/${kit.team_id}?tab=kit`)}>
          ← Back to Kit Dashboard
        </button>

        <div className="ok-eyebrow">[ Order ]</div>
        <h1 className="ok-title">Order your kit.</h1>
        <p className="ok-subtitle">Select your sizes and proceed to checkout.</p>

        <div className="ok-layout">

          {/* LEFT — Kit preview */}
          <div className="ok-card">
            <div className="ok-card-title">Kit Design</div>

            {(kit.preview_front_url || kit.preview_back_url) && (
              <div className="ok-preview-pair">
                {kit.preview_front_url && (
                  <div className="ok-preview-block">
                    <div className="ok-preview-label">Front</div>
                    <img src={kit.preview_front_url} alt="Kit Front" className="ok-preview-img" />
                  </div>
                )}
                {kit.preview_back_url && (
                  <div className="ok-preview-block">
                    <div className="ok-preview-label">Back</div>
                    <img src={kit.preview_back_url} alt="Kit Back" className="ok-preview-img" />
                  </div>
                )}
              </div>
            )}

            <div className="ok-spec-table">
              <div className="ok-spec-row">
                <span className="ok-spec-label">Design</span>
                <span className="ok-spec-value">{kit.design_name}</span>
              </div>
              <div className="ok-spec-row">
                <span className="ok-spec-label">Type</span>
                <span className="ok-spec-value">{kit.kit_type === "polo" ? "Polo Shirt" : "T-Shirt"}</span>
              </div>
              <div className="ok-spec-row">
                <span className="ok-spec-label">Colour</span>
                <span className="ok-spec-value ok-spec-color">
                  <span className="ok-color-dot" style={{ backgroundColor: kit.teamwear_color || "#1E3A8A" }} />
                  {kit.teamwear_color || "#1E3A8A"}
                </span>
              </div>
              {kit.back_print_enabled && kit.back_print_text && (
                <div className="ok-spec-row">
                  <span className="ok-spec-label">Back Print</span>
                  <span className="ok-spec-value">"{kit.back_print_text}"</span>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — Quantities, address, checkout */}
          <div className="ok-right">

            <div className="ok-card">
              <div className="ok-card-title">Select Quantities</div>
              <div className="ok-sizes">
                {sizes.map(size => (
                  <div key={size} className="ok-size-row">
                    <span className="ok-size-label">{size}</span>
                    <div className="ok-qty">
                      <button className="ok-qty-btn" onClick={() => updateQuantity(size, quantities[size] - 1)}>−</button>
                      <span className="ok-qty-val">{quantities[size]}</span>
                      <button className="ok-qty-btn" onClick={() => updateQuantity(size, quantities[size] + 1)}>+</button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="ok-summary">
                <div className="ok-summary-row">
                  <span>Items ({totalItems}) @ £{unitPrice.toFixed(2)} each (inc. VAT)</span>
                  <span>£{total.toFixed(2)}</span>
                </div>
                <div className="ok-summary-row ok-summary-total">
                  <span>Total (inc. VAT)</span>
                  <span>£{total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="ok-card">
              <div className="ok-card-title">Delivery Address</div>
              <div className="ok-address-form">
                <div className="ok-field ok-field-full">
                  <label className="ok-field-label">Address line 1 <span className="ok-required">*</span></label>
                  <input type="text" className="ok-input" placeholder="Street address"
                    value={address.line1} onChange={e => setAddress(prev => ({ ...prev, line1: e.target.value }))} />
                </div>
                <div className="ok-field ok-field-full">
                  <label className="ok-field-label">Address line 2</label>
                  <input type="text" className="ok-input" placeholder="Apartment, suite, unit (optional)"
                    value={address.line2} onChange={e => setAddress(prev => ({ ...prev, line2: e.target.value }))} />
                </div>
                <div className="ok-field">
                  <label className="ok-field-label">City <span className="ok-required">*</span></label>
                  <input type="text" className="ok-input" placeholder="City"
                    value={address.city} onChange={e => setAddress(prev => ({ ...prev, city: e.target.value }))} />
                </div>
                <div className="ok-field">
                  <label className="ok-field-label">Postcode <span className="ok-required">*</span></label>
                  <input type="text" className="ok-input" placeholder="e.g. SW1A 1AA"
                    value={address.postcode} onChange={e => setAddress(prev => ({ ...prev, postcode: e.target.value }))} />
                </div>
              </div>
            </div>

            <div className="ok-checkout-row">
              <div className="ok-stripe-notice">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                </svg>
                Secure checkout powered by Stripe
              </div>
              <button
                className="ok-checkout-btn"
                onClick={handleCheckout}
                disabled={totalItems === 0 || isSubmitting}
              >
                {isSubmitting ? "Redirecting to Stripe…" : `Pay £${total.toFixed(2)} →`}
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderKit;
